import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GoogleMap from "./GoogleMap";
import { api } from "../../utils/api";
import "./Dashboard.css";
import Layout from "../Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SurveyViewModal from "./SurveyViewModal";

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  // recentSurveys will be populated from the API; remove hard-coded dummy data
  const [recentSurveys, setRecentSurveys] = useState([]);
  const [surveyData, setSurveyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [propertyPolygons, setPropertyPolygons] = useState([]);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewSurvey, setViewSurvey] = useState(null);

  // Dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const statsData = await api.getDashboardStats();
        setStats(statsData);
      } catch (error) {
        toast.error(error.message || "Failed to load dashboard stats");
      }
    };
    fetchStats();
  }, []);

  // Properties (for polygons)
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const propertiesResp = await api.getProperties();
        let propertiesList = [];
        if (Array.isArray(propertiesResp)) {
          propertiesList = propertiesResp;
        } else if (propertiesResp && Array.isArray(propertiesResp.data)) {
          propertiesList = propertiesResp.data;
        }
        const polygons = propertiesList
          .map((p) => {
            const coords =
              p.polygon_coords ||
              p.polygon ||
              p.polygonCoords ||
              p.polygonCoordinates;
            if (!Array.isArray(coords) || coords.length === 0) return null;
            const normalized = coords
              .map((c) => {
                if (Array.isArray(c) && c.length >= 2) {
                  const a = Number(c[0]);
                  const b = Number(c[1]);
                  if (Number.isFinite(a) && Number.isFinite(b)) {
                    if (Math.abs(a) <= 90 && Math.abs(b) <= 180)
                      return { lat: a, lng: b };
                    return { lat: b, lng: a };
                  }
                  return null;
                }
                const lat = Number(
                  c.lat ?? c.latitude ?? c.lat_float ?? c.lat_deg
                );
                const lng = Number(
                  c.lng ?? c.longitude ?? c.lng_float ?? c.lon ?? c.lng_deg
                );
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                return { lat, lng };
              })
              .filter(Boolean);
            if (normalized.length === 0) return null;
            return { ...p, polygon: normalized };
          })
          .filter(Boolean);
        setPropertyPolygons(polygons);
      } catch (error) {
        toast.error(error.message || "Failed to load properties");
      }
    };
    fetchProperties();
  }, []);

  // Surveys (for table)
  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        const surveysResp = await api.getSurveys();
        setSurveyData(surveysResp?.data?.data);
      } catch (error) {
        toast.error(error.message || "Failed to load surveys");
      }
    };
    fetchSurveys();
  }, []);

  const handleViewSurvey = async (id) => {
    try {
      const res = await api.getSurveyById(id);
      console.log("Fetched survey details:", res);
      setViewSurvey(res.data || res);
      setViewModalVisible(true);
    } catch (err) {
      toast.error(err.message || "Failed to fetch survey details");
    }
  };

  return (
    <Layout>
      <SurveyViewModal
        visible={viewModalVisible}
        onClose={() => setViewModalVisible(false)}
        survey={viewSurvey}
      />
      <div className="dashboard">
        <div className="dashboard-content">
          <main className="dashboard-main">
            <div className="overview-content">
              <div className="content-header">
                <h2>Dashboard</h2>
                <p>Welcome to your Safe City Survey Portal dashboard</p>
              </div>

              <div className="stats-grid">
                {stats && stats.data ? (
                  <>
                    <div className="stat-card">
                      <div className="stat-header">
                        <h3>Total Surveys</h3>
                      </div>
                      <div className="stat-value">
                        {stats.data.total_surveys}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-header">
                        <h3>Total Users</h3>
                      </div>
                      <div className="stat-value">{stats.data.total_users}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-header">
                        <h3>Portal Users</h3>
                      </div>
                      <div className="stat-value">
                        {stats.data.portal_users}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-header">
                        <h3>App Users</h3>
                      </div>
                      <div className="stat-value">{stats.data.app_users}</div>
                    </div>
                  </>
                ) : (
                  <div>Loading stats...</div>
                )}
              </div>

              <div className="content-section">
                <h3>Recent House Surveys</h3>
                <div className="surveys-table">
                  <div className="table-header">
                    <div>Address</div>
                    <div>City</div>
                    <div>Status</div>
                    <div>Role in Property</div>
                    <div>First Name</div>
                    <div>Actions</div>
                  </div>
                  {Array.isArray(surveyData) && surveyData.length > 0 ? (
                    surveyData.map((survey, sIdx) => {
                      const firstPerson =
                        Array.isArray(survey.persons) &&
                        survey.persons.length > 0
                          ? survey.persons[0]
                          : null;
                      return (
                        <div className="table-row" key={`survey-${sIdx}`}>
                          <div>{survey.address_line || ""}</div>
                          <div>{survey.city || ""}</div>
                          <div>{survey.status || ""}</div>
                          <div>
                            {firstPerson
                              ? firstPerson.role_in_property || ""
                              : ""}
                          </div>
                          <div>
                            {firstPerson ? firstPerson.first_name || "" : ""}
                          </div>
                          <div className="action-buttons">
                            <button
                              className="btn-action"
                              onClick={() => handleViewSurvey(survey.id)}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="table-row">
                      <div colSpan={5}>No survey data found</div>
                    </div>
                  )}
                </div>
              </div>

              <GoogleMap
                polygons={propertyPolygons}
                setPolygons={setPropertyPolygons}
              />
            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
